/**
 * Opta controller onboarding API service.
 *
 * Wraps the admin, farm-scoped controller endpoints (create a pending
 * controller + issue a claim code, list, regenerate code). The device-side
 * claim (`POST /controllers/claim`) is called by the firmware itself, not the
 * web app. See docs/OPTA_Control/BACKEND_FRONTEND_PLAN.md §6.
 */
import { apiClient } from './apiClient';
import type { IoLive } from './optaStationApi';

export type ControllerStatus = 'pending' | 'claimed' | 'expired' | 'revoked';

export interface ControllerClaimSummary {
  id: number;
  farm_id: string;
  name: string | null;
  ctrl_uid: string;
  finca_key: string;
  status: ControllerStatus;
  /** Present only while pending (bearer secret). */
  claim_code: string | null;
  claim_code_expires_at: string | null;
  device_id: number | null;
  created_at: string;
  claimed_at: string | null;
  /** Retired and dismissed from the list. Hidden unless explicitly requested. */
  archived_at: string | null;
  /** Talking right now (live status, heard within the offline-alert window).
   *  NOT the same as `status === 'claimed'`, which only means "paired once". */
  online?: boolean | null;
  last_seen?: string | null;
}

export interface ControllerCreate {
  name?: string;
}

export interface PumpDemandProgram {
  entity_id: number;
  name: string | null;
  program_id: number | null;
  enabled: boolean;
}

/** What can start a pump (backend core/services/pump_demand.py). */
export interface PumpDemand {
  output_id: number;
  pump_station_id: number;
  role: string;
  label: string | null;
  runs_with_programs: boolean;
  programs: PumpDemandProgram[];
  /** Farm-local starts, days, integer-second amount — as stored on the platform. */
  calendar: {
    starts: string[]; days?: string[] | null; amount_s: number;
    duty_on_s?: number | null; duty_off_s?: number | null;
  } | null;
  calendar_on_device: boolean;
  /** Platform and controller disagree about the calendar (unsent or unacked). */
  calendar_pending: boolean;
  /** ISO with the farm's offset; set while a calendar firing is under way. */
  firing_until: string | null;
  next_start: string | null;
  /** Reservoirs this pump fills. */
  fills: string[];
  /** Sector names it serves; [] = every sector of its head. */
  serves: string[];
  protected: boolean;
  timezone: string;
}

export interface ControllerEntityState {
  tipo: string;
  entity_id: number;
  state: Record<string, any> | null;
  ts: string | null;
  updated_at: string;
  /** pump/sector control mode from the last acked command: auto|manual_on|manual_off. */
  mode?: string | null;
  /** The platform's name for this entity, from the last published cfg. */
  name?: string | null;
  /**
   * Sectors only. 'local' = the head drives this sector's own relay.
   * 'remote' = the head NAMES it but its valve is a UC51x it cannot reach, so
   * `state.on` is a structural false and `sector/manual` reaches no output.
   */
  binding?: 'local' | 'remote' | null;
  /**
   * Why the output is where it is (`dry_run`, `interlock`, `override`,
   * `restricted`…), promoted out of the raw `state` blob beside `mode`.
   */
  reason?: string | null;
  /**
   * Pumps only: the restrictions gating this one, each with its live verdict.
   * Assembled server-side — the join is easy to get wrong, and getting it wrong
   * means showing a restriction against the wrong pump, which is worse than
   * showing none.
   */
  rules?: RuleVerdict[] | null;
  /**
   * Pumps only: everything that can start this pump — programs whose published
   * `pumps[]` name it, its own calendar (next start / end of the firing under
   * way), the reservoirs it fills. Combined on the client with the live `mode`.
   */
  demand?: PumpDemand | null;
  /**
   * The platform object behind this entity was DELETED — this is a tombstone.
   * Only ever true when the state was fetched with `include_retired`; the normal
   * listing omits these rows entirely.
   *
   * "Retired" is not "gone": SOIP has no delete, so the controller still holds
   * the entity in flash and (until the firmware skips disabled entities) still
   * republishes its state every 10 s. A diagnostics view showing "retired on the
   * 12th, the equipment is still announcing it" is the only place that fact
   * surfaces.
   */
  retired?: boolean;
  retired_at?: string | null;
  /**
   * The platform object behind this entity, where the cfg `name` is not good
   * enough to show. Programs are the case: the firmware caps a name at 31 chars,
   * so «Programación riego agronic (4-6)» arrives as
   * «Programación riego agronic (4-6» — cut mid-word.
   *
   * Absent when the platform row is gone; `name` (what the DEVICE was told it is
   * called) is the honest fallback there.
   */
  source_name?: string | null;
  source_id?: number | null;
}

/**
 * One restriction's live verdict, as rendered NEXT TO THE PUMP.
 *
 * This breakdown is the reason `rule/{id}/state` is published at all: "why is
 * the pump not running" becomes one read instead of a phone call to the farm.
 */
export interface RuleVerdict {
  entity_id: number;
  /** When this rule state was RECEIVED. `since_s` and each term's `age_s` are
   *  relative to that message, not to now. */
  observed_at?: string | null;
  name?: string | null;
  /** THREE-valued and BEFORE `on_unknown`. */
  verdict?: 'allow' | 'deny' | 'unknown' | null;
  /** The resolved boolean, AFTER `on_unknown` and after `hold`. */
  allowed?: boolean | null;
  enforce?: 'auto_only' | 'hard' | string | null;
  since_s?: number | null;
  /**
   * POSITIONAL against the published all[]/any[] — index into it, never re-order
   * or filter it. `ok:false` means the condition is not met; `unknown:true`
   * (present only when true) means there is nothing to judge it by. They are
   * different things and lead an operator to different places.
   */
  terms?: RuleTermState[] | null;
}

export interface RuleTermState {
  when?: string;
  ok?: boolean;
  unknown?: boolean;
  value?: number | null;
  age_s?: number | null;
}

/** One remote sector's expected-vs-observed comparison. */
export interface RemoteSector {
  sector_id: number;
  sector_name: string | null;
  entity_id: number;
  dev_eui: string | null;
  valve_number: number | null;
  device_name: string | null;
  expected_open: boolean | null;
  program_id: number | null;
  program_name: string | null;
  remaining_s: number | null;
  override: string | null;
  observed_status: string | null;
  observed_age_s: number | null;
  verdict: 'agree' | 'starved' | 'stray' | 'orphan' | 'unknown';
  episode_opened_at: string | null;
  episode_dwell_s: number | null;
}

export interface RemoteSectorsResponse {
  ctrl_uid: string | null;
  /** False = the supervisor is reporting only and will not correct anything. */
  enforced: boolean;
  sectors: RemoteSector[];
}

/** One numeric series: a single field of a single entity, over time. */
export interface OptaHistorySeries {
  /** `${tipo}/${entity_id}/${field}` — the canonical tipo, so `pump`, never `bomba`. */
  key: string;
  tipo: string;
  entity_id: number;
  field: string;
  unit: string | null;
  points: Array<{
    t: string;
    v: number | null;
    /** Bucket min/max. Present so a transient survives bucketing — a 1-minute
     *  mean hides the pressure spike that is the reason to look. */
    lo: number | null;
    hi: number | null;
  }>;
}

/** One discrete state span, for the swimlane. `to: null` means still in it. */
export interface OptaHistoryInterval {
  key: string;
  tipo: string;
  entity_id: number;
  field: string;
  value: string;
  from: string;
  to: string | null;
}

export interface OptaHistoryEvent {
  t: string;
  code: string;
  sev: string;
  tipo: string | null;
  entity_id: number | null;
  msg: string | null;
}

export interface OptaSensorLimit {
  rule_id: number;
  rule_name: string | null;
  sensor_id: number;
  /** Starts denying above / below this; allows again below / above the other. */
  deny_above: number | null;
  allow_below: number | null;
  deny_below: number | null;
  allow_above: number | null;
}

/** One controller on the sensors page, titled by the heads it drives. */
export interface FarmHeadCard {
  claim_id: number;
  device_id: number;
  ctrl_uid: string;
  controller_name: string;
  last_seen: string | null;
  /** Empty for a controller that drives no head (sensors only). */
  stations: Array<{ id: number; name: string }>;
}

export interface OptaHistoryResponse {
  ctrl_uid: string;
  time_range: { start: string; end: string };
  /** 'raw' or 'rollup' — chosen server-side from the window. */
  source: 'raw' | 'rollup';
  interval: string | null;
  series: OptaHistorySeries[];
  intervals: OptaHistoryInterval[];
  /** `${canonical tipo}/${entity_id}` → display name, from the published cfg. */
  names?: Record<string, string>;
  /** Warn/alarm events (and restriction flips) in the window, oldest first. */
  events?: OptaHistoryEvent[];
  /** Restriction limits on sensors, as lines to draw on their chart. */
  limits?: OptaSensorLimit[];
  /** What this controller actually published in the window. Discovery is by
   *  RECENCY: a ghost entity the firmware refuses to delete has no rows and so
   *  never reaches the chart. */
  available: {
    metrics: Array<{ key: string; tipo: string; entity_id: number; field: string; unit: string | null }>;
    states: Array<{ key: string; tipo: string; entity_id: number; field: string }>;
  };
}

export interface RemoteSectorOverrideResult {
  sector_id: number;
  mode: string;
  /**
   * The valve command actually sent. The hold and the downlink are one action:
   * a 503 means nothing was recorded either, so the button cannot look like it
   * worked while the valve never heard it.
   */
  command_id: string | null;
  /** When a manual_on hold lapses — it expires with the valve's countdown. */
  expires_at: string | null;
}

export interface RemoteSectorReconcileResult {
  sector_id: number;
  action: string;
  verdict: string;
  command_id: string | null;
  detail: string | null;
}

export interface ControllerEvent {
  tipo: string | null;
  entity_id: number | null;
  sev: string;
  code: string;
  msg: string | null;
  ts: string | null;
  created_at: string;
}

export interface ControllerStateResponse {
  ctrl_uid: string;
  online: boolean;
  status: string | null;
  fw_rev: string | null;
  ota_state: string | null;
  /**
   * OBSERVED maintenance state, not something the platform owns: while true the
   * controller has dropped every output and WILL NOT IRRIGATE. Raised by our own
   * `maintenance` command, by a technician on the board's serial console, or by
   * the firmware itself for the duration of an OTA. Cross-check `ota_state`
   * (`downloading`/`applying`) to tell an upgrade from an operator action.
   */
  maintenance: boolean;
  last_seen: string | null;
  caps: Record<string, any> | null;
  entities: ControllerEntityState[];
  events: ControllerEvent[];
}

export interface CommandRequest {
  tipo: 'program' | 'sector' | 'pump' | 'controller';
  entity_id?: number | null;
  verb: string;
  args?: Record<string, any>;
}

export interface CommandResponse {
  req_id: string;
  tipo: string;
  entity_id: number | null;
  verb: string;
  state: string;
}

/**
 * An entity the controller holds that no platform object owns.
 *
 * It exists because the controller keeps its own config in flash: anything
 * published straight at it (a test cfg, and in future an on-site HMI) shows up in
 * live state while being absent from every configuration tab — and undeletable,
 * because the delete path edits a platform row that never existed.
 *
 * `status` decides what can be done:
 *  - `adoptable`     — its cfg was captured, so a platform row can be built.
 *  - `not_adoptable` — compiled from a richer platform object (program, sector,
 *                      dosing, recipe) that a device cfg cannot reconstruct.
 *  - `no_cfg`        — only ever seen in `state`; its retained cfg is gone, so
 *                      there is nothing to build from.
 */
export interface UnownedEntity {
  tipo: string;
  entity_id: number;
  name: string | null;
  cfg: Record<string, any> | null;
  observed_rev: number | null;
  /**
   * 'adoptable'     — we hold its cfg, so a platform row can be built from it.
   * 'not_adoptable' — a kind that is compiled from platform objects.
   * 'no_cfg'        — only ever seen in `state`; nothing to adopt from.
   * 'orphaned'      — the dangerous one. Still ENABLED on the controller with no
   *                   platform row behind it: a delete whose disable never got
   *                   through, so it may still be holding a relay.
   */
  status: 'adoptable' | 'not_adoptable' | 'no_cfg' | 'orphaned';
  reason: string | null;
  first_seen: string | null;
}

export interface AdoptResult {
  tipo: string;
  entity_id: number;
  source_kind: string;
  source_id: number;
  /** What the cfg could not express and the operator should now fill in. */
  notes: string[];
  /** The entity id sits outside the controller's pool for its type — exactly how
   *  the `tanque/9` ghost arose. Worth saying out loud. */
  id_outside_pool: boolean;
}

/** GET /controllers/farms/{farmId}/claims/{claimId}/reboot-preview */
export interface RebootPreview {
  ctrl_uid: string;
  /** Firmware announces `caps.reboot`. False = the verb would do nothing. */
  supported: boolean;
  fw_rev: string | null;
  last_seen_age_s: number | null;
  state_age_s: number | null;
  uptime_s: number | null;
  pumps: {
    entity_id: number;
    role: string | null;
    on: boolean | null;
    mode: string | null;
    age_s: number | null;
    /** In manual_on: overrides survive a reboot, so it can come back running. */
    may_restart_on: boolean;
  }[];
  any_pump_may_restart_on: boolean;
}

/** One silence between two heartbeats, and the layer it was lost in. */
export interface LinkGap {
  start: string;
  end: string;
  seconds: number;
  /** reboot: restarted inside it · link: broker last-will fired · silent: session up, nothing published */
  class: 'reboot' | 'link' | 'silent' | null;
}

export interface LinkHealthResponse {
  ctrl_uid: string;
  time_range: { start: string; end: string };
  summary: {
    samples: number;
    gaps: number;
    gaps_by_class: Record<string, number>;
    longest_gap_s: number;
    reboots: number;
    offline_episodes: number;
    offline_s: number;
    online_pct: number;
    currently_offline: boolean;
  };
  gaps: LinkGap[];
  offline: { start: string; end: string | null; seconds: number }[];
  boots: { t: string; sev: string; msg: string | null }[];
  uptime: { t: string; uptime_s: number }[];
  /** The firmware's own `status.diag` block, when it sends one. */
  latest_diag: Record<string, unknown> | null;
  latest_dropped: { out: number; in: number } | null;
}

export const optaApi = {
  createController(farmId: string, body: ControllerCreate): Promise<ControllerClaimSummary> {
    return apiClient.post<ControllerClaimSummary>(`/controllers/farms/${farmId}`, body);
  },

  listControllers(
    farmId: string,
    includeArchived = false,
  ): Promise<ControllerClaimSummary[]> {
    const q = includeArchived ? '?include_archived=true' : '';
    return apiClient.get<ControllerClaimSummary[]>(`/controllers/farms/${farmId}${q}`);
  },

  /**
   * Issue a fresh claim code. On an already-claimed controller this is the
   * re-pair path: same identity and history, new code, and fresh MQTT
   * credentials once the device claims with it (use it to move a controller to
   * another broker or to recover lost credentials).
   */
  regenerateCode(farmId: string, claimId: number): Promise<ControllerClaimSummary> {
    return apiClient.post<ControllerClaimSummary>(
      `/controllers/farms/${farmId}/claims/${claimId}/regenerate-code`,
    );
  },

  /**
   * Decommission a controller: revokes its broker credentials and releases its
   * board so it can be claimed again as a new controller. History is kept (the
   * device row is deactivated, not deleted). `force` proceeds even if the
   * broker credentials could not be revoked.
   */
  decommissionController(
    farmId: string,
    claimId: number,
    force = false,
  ): Promise<ControllerClaimSummary> {
    return apiClient.delete<ControllerClaimSummary>(
      `/controllers/farms/${farmId}/claims/${claimId}${force ? '?force=true' : ''}`,
    );
  },

  /**
   * Remove a retired controller from the farm's list.
   *
   * A claim that was never used is deleted; one that owns a Device must already
   * be decommissioned and is ARCHIVED — the Device row and all its irrigation
   * history survive. 409 while it still drives a pump station: that head has to
   * be migrated to another controller first.
   */
  forgetController(farmId: string, claimId: number): Promise<void> {
    return apiClient.delete<void>(
      `/controllers/farms/${farmId}/claims/${claimId}/forget`,
    );
  },

  getState(farmId: string, claimId: number): Promise<ControllerStateResponse> {
    return apiClient.get<ControllerStateResponse>(
      `/controllers/farms/${farmId}/claims/${claimId}/state`,
    );
  },

  /**
   * Live input-terminal readings for a claimed controller.
   *
   * Same payload as the head-scoped endpoint (see IoLive in optaStationApi):
   * this one is addressed by claim, because the cockpit knows a controller, not
   * a station — and its terminals belong to no single head.
   */
  ioLive(farmId: string, claimId: number): Promise<IoLive> {
    return apiClient.get<IoLive>(
      `/controllers/farms/${farmId}/claims/${claimId}/io-live`,
    );
  },

  rebootPreview(farmId: string, claimId: number): Promise<RebootPreview> {
    return apiClient.get<RebootPreview>(
      `/controllers/farms/${farmId}/claims/${claimId}/reboot-preview`,
    );
  },

  sendCommand(
    farmId: string,
    claimId: number,
    body: CommandRequest,
  ): Promise<CommandResponse> {
    return apiClient.post<CommandResponse>(
      `/controllers/farms/${farmId}/claims/${claimId}/command`,
      body,
    );
  },

  /**
   * Telemetry history: numeric series + discrete state spans on one axis.
   * Omit `interval` for raw samples. The source (raw hypertable vs 1-minute
   * rollup) is decided server-side from the window.
   */
  history(
    farmId: string, claimId: number,
    opts: { hours: number; interval?: string; fields?: string[]; from?: Date; to?: Date },
  ): Promise<OptaHistoryResponse> {
    const p = new URLSearchParams({ hours: String(opts.hours) });
    // An explicit window wins server-side over `hours`.
    if (opts.from) p.set('from', opts.from.toISOString());
    if (opts.to) p.set('to', opts.to.toISOString());
    if (opts.interval) p.set('interval', opts.interval);
    if (opts.fields?.length) p.set('fields', opts.fields.join(','));
    return apiClient.get<OptaHistoryResponse>(
      `/controllers/farms/${farmId}/claims/${claimId}/history?${p.toString()}`,
    );
  },

  /** Silences, reboots and offline stretches from the kept heartbeats. Viewer+. */
  linkHealth(farmId: string, claimId: number, days: number): Promise<LinkHealthResponse> {
    return apiClient.get<LinkHealthResponse>(
      `/controllers/farms/${farmId}/claims/${claimId}/link-health?days=${days}`,
    );
  },

  /** Claimed controllers with the heads they drive (sensors page). Viewer+. */
  listFarmHeads(farmId: string): Promise<FarmHeadCard[]> {
    return apiClient.get<FarmHeadCard[]>(`/controllers/farms/${farmId}/heads`);
  },

  /** Head intent vs real UC51x valve state, per remote sector. Viewer+. */
  listRemoteSectors(farmId: string, claimId: number): Promise<RemoteSectorsResponse> {
    return apiClient.get<RemoteSectorsResponse>(
      `/controllers/farms/${farmId}/claims/${claimId}/remote-sectors`,
    );
  },

  /**
   * Honour the expected status for one remote sector now. Operator+.
   * Available even when the supervisor is in report-only mode — that is how the
   * correction earns trust before being armed. Refuses on a stale reading.
   */
  reconcileRemoteSector(
    farmId: string, claimId: number, sectorId: number,
  ): Promise<RemoteSectorReconcileResult> {
    return apiClient.post<RemoteSectorReconcileResult>(
      `/controllers/farms/${farmId}/claims/${claimId}/remote-sectors/${sectorId}/reconcile`,
    );
  },

  /**
   * Hold a remote sector open/shut, or hand it back to auto. Operator+.
   * `manual_on` REQUIRES a duration: an open with no auto-close strands the
   * valve if the next command is lost.
   */
  setRemoteSectorOverride(
    farmId: string, claimId: number, sectorId: number,
    mode: 'manual_on' | 'manual_off' | 'auto',
    durationSeconds?: number,
  ): Promise<RemoteSectorOverrideResult> {
    return apiClient.put<RemoteSectorOverrideResult>(
      `/controllers/farms/${farmId}/claims/${claimId}/remote-sectors/${sectorId}/override`,
      { mode, duration_seconds: durationSeconds ?? null },
    );
  },

  listUnowned(farmId: string, claimId: number): Promise<{ entities: UnownedEntity[] }> {
    return apiClient.get<{ entities: UnownedEntity[] }>(
      `/controllers/farms/${farmId}/claims/${claimId}/unowned-entities`,
    );
  },

  /** Take ownership: creates the platform row, publishes nothing. Admin+. */
  adoptUnowned(
    farmId: string, claimId: number, tipo: string, entityId: number,
  ): Promise<AdoptResult> {
    return apiClient.post<AdoptResult>(
      `/controllers/farms/${farmId}/claims/${claimId}/unowned-entities/${tipo}/${entityId}/adopt`,
    );
  },

  /**
   * Finish an interrupted delete: republish the entity's cfg with
   * `enabled:false` (SOIP's only deletion) and bury it. Admin+.
   *
   * Unlike adoption this DOES change the equipment — an orphan may still be
   * holding a relay, which is why it is an explicit action and not a sweep.
   */
  retireUnowned(
    farmId: string, claimId: number, tipo: string, entityId: number,
  ): Promise<{ tipo: string; entity_id: number; retired: boolean }> {
    return apiClient.post(
      `/controllers/farms/${farmId}/claims/${claimId}/unowned-entities/${tipo}/${entityId}/retire`,
    );
  },
};
